import { ApiError, Audience, BuildAudience } from "@/types";

/**
 * Returns the data from an API response, or throws its error so it can be
 * caught by the action workflow.
 */
export const unwrap = <T>(response: { data: T | null; error: ApiError | null }): T => {
  if (response.error) throw new Error(response.error.message);
  return response.data as T;
};

/**
 * Path to the list page for an audience's type, e.g. "/audience" or "/upload".
 */
export const audienceListPath = (audience: Pick<Audience, "audience_type">) =>
  audience.audience_type === "build" ? "/audience" : `/${audience.audience_type}`;

/**
 * Absolute path to an audience, optionally to one of its sub pages,
 * e.g. "/audience/123" or "/upload/123/insight".
 */
export const audiencePath = (
  audience: Pick<Audience, "audience_type" | "audience_id">,
  segment?: "insight" | "export"
) => [audienceListPath(audience), audience.audience_id, segment].filter(Boolean).join("/");

/**
 * Converts a build audience's variable groups into API query strings,
 * e.g. "AND(1,2)". Empty groups are skipped.
 */
export const buildQueries = (audience: Pick<BuildAudience, "var_groups">) =>
  audience.var_groups
    .filter((group) => group.variables.length > 0)
    .map((group) => `${group.operator}(${group.variables.map((v) => v.tag_id).join(",")})`);

/**
 * Converts a build audience's geo fence into a comma separated list of geo ids.
 */
export const buildGeoFence = (audience: Pick<BuildAudience, "geo_fence">) =>
  audience.geo_fence?.map((x) => x.geo_id).join(",");

/**
 * Whether any of a build audience's variable groups has variables.
 */
export const hasVariables = (audience?: Pick<BuildAudience, "var_groups"> | null) =>
  !!audience?.var_groups?.some((group) => group.variables.length > 0);
